/**
 * UNIFIED-SHELL-AND-ROLE-NAVIGATION-V1 (V7-P4 Slice 2) -- the ONE breadcrumb
 * primitive.
 *
 * The "breadcrumbs" experience rule: every deep surface shows the SAME trail
 * grammar -- an ordered list of crumbs, earlier crumbs are links, the final
 * crumb is the current page (aria-current, never a link). Generic and
 * business-logic-free (the PORTABILITY TEST): a crumb is just a `{ label, to }`
 * pair, so Lead / Patient / Appointment / Visit / Treatment / Payment / Case
 * Sheet trails all reuse it with no redesign. Navigation only -- a breadcrumb
 * never widens authority; it points at destinations the caller already reaches.
 *
 * Accessibility: a labelled `nav` landmark, an ordered list, and
 * `aria-current="page"` on the terminal crumb. The separator is decorative
 * (aria-hidden) so screen readers announce only the crumb labels.
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

export interface BreadcrumbItem {
  readonly label: ReactNode;
  /** Destination. Omit (or leave undefined) for the current-page crumb. */
  readonly to?: string;
  /** Optional stable key when labels are not unique strings. */
  readonly key?: string;
}

export interface BreadcrumbsProps {
  readonly items: readonly BreadcrumbItem[];
  readonly testId?: string;
}

export default function Breadcrumbs({
  items,
  testId,
}: BreadcrumbsProps): JSX.Element | null {
  if (items.length === 0) return null;
  const lastIndex = items.length - 1;
  return (
    <nav
      className="ac-breadcrumbs"
      aria-label="Breadcrumb"
      data-testid={testId}
    >
      <ol className="ac-breadcrumbs__list">
        {items.map((item, index) => {
          const isCurrent = index === lastIndex;
          const key = item.key ?? `${index}`;
          return (
            <li className="ac-breadcrumbs__item" key={key}>
              {!isCurrent && item.to ? (
                <Link className="ac-breadcrumbs__link" to={item.to}>
                  {item.label}
                </Link>
              ) : (
                <span
                  className="ac-breadcrumbs__current"
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  {item.label}
                </span>
              )}
              {!isCurrent ? (
                <span className="ac-breadcrumbs__sep" aria-hidden="true">
                  /
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
